import ContrastIcon from "@mui/icons-material/Contrast";
import { Alert, Box, Button, Stack, TextField } from "@mui/material";
import { getContrastRatio } from "@mui/material/styles";
import { useState } from "react";
import { HexColorPicker } from "react-colorful";

import type { FC } from "react";

/** A hex colour as "#RRGGBB", with or without its "#", or null when it is not one. */
const parseHex = (text: string): string | null => {
  const hex = text.trim().replace(/^#?/, "#");
  return /^#[0-9a-f]{6}$/i.test(hex) ? hex.toUpperCase() : null;
};

/** Labels in a colour on white need a contrast of 4.5:1 to be read. */
const tooLight = (hex: string): boolean => getContrastRatio(hex, "#FFFFFF") < 4.5;

/**
 * A colour picker and a hex field, to use a colour that is not in the list. It warns when the
 * colour is too light to read on white, and uses a valid colour only.
 */
export const CustomColourPicker: FC<{
  start: string;
  onUse: (hex: string) => void;
  onCancel: () => void;
}> = ({ start, onUse, onCancel }) => {
  const [text, setText] = useState(start.toUpperCase());
  const hex = parseHex(text);
  const shown = hex ?? start;

  return (
    <Stack
      role="group"
      aria-label="Custom colour picker"
      spacing={1.75}
      sx={{
        p: 2,
        border: 1,
        borderColor: "divider",
        borderRadius: "8px",
        boxShadow: 3,
        "& .react-colorful": { width: "100%" },
      }}
    >
      <HexColorPicker color={shown} onChange={(value) => setText(value.toUpperCase())} />
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "flex-start" }}>
        <Box
          aria-hidden
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: "8px",
            border: 1,
            borderColor: "divider",
            bgcolor: shown,
          }}
        />
        <TextField
          label="Hex"
          size="small"
          fullWidth
          value={text}
          onChange={(event) => setText(event.target.value)}
          error={!hex}
          slotProps={{ htmlInput: { maxLength: 7, spellCheck: false } }}
        />
      </Stack>
      {hex && tooLight(hex) ? (
        <Alert severity="warning" icon={<ContrastIcon fontSize="small" />}>
          This colour is hard to read on white. Pick a darker shade so labels stay legible.
        </Alert>
      ) : null}
      <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
        <Button onClick={onCancel} data-analytics-id="program-edit-custom-colour-cancel">
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!hex}
          onClick={() => {
            if (hex) onUse(hex);
          }}
          data-analytics-id="program-edit-custom-colour-use"
        >
          Use colour
        </Button>
      </Stack>
    </Stack>
  );
};
